import toArray from "lodash/toArray";

import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonRadioButtonQuestion } from "@/storybook-components";

import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

interface RadioButtonQuestionProps {
  question: Question;
}

const RadioButtonQuestion = ({
  question
}: RadioButtonQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, saving } = survey;

  const error = errors[question.id] ?? {};
  const answer = answers[question.id] ?? {};

  // ToDo: remove from BE
  const sortedOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  const handleChange = (value: string): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: parseInt(value)
        }
      })
    );
  };

  const handleOtherChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: answer.id,
          value: e.target.value
        }
      })
    );
  };

  const showTextAnswer = sortedOptions.find(
    (option) => option.supportsTextAnswer && answer?.id === option.id
  );

  const minImageWidth = 300;

  return (
    <CommonRadioButtonQuestion
      layoutProps={{ width: "100%", as: "fieldset" }}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelType="legend"
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      questionId={question?.id}
      contentLayoutProps={{ py: 4 }}
      isHorizontal={question.horizontal}
      answer={answer?.id}
      minImageWidth={minImageWidth}
      handleChange={handleChange}
      size="xlIconWithLabelStyle"
      answerStackSpacing={spacing.answerStack}
      options={sortedOptions}
      answerProps={{ ...textStyles?.content }}
      showTextAnswer={showTextAnswer}
      isDisabled={saving}
      otherAnswer={answer?.value ?? ""}
      handleOtherAnswer={handleOtherChange}
      otherAnswerClassName="ehq-surveyTextarea"
      otherAnswerSize="lg"
      otherAnswerProps={{ ...textStyles?.content, mt: 2 }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default RadioButtonQuestion;
